<template>
  <div class="employee-payments-table">
    <data-table
      ref="datatableRef"
      testIdPrefix="employee-payments"
      displayMode="table"
      :config="datatableConfig"
      :data="payments"
      @button-click="handleButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEmployeePayroll } from '@/composables/useEmployeePayroll';
import { permissionsService } from '@/services/permissionsService';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import DataTable from '@/grid/DataTable.vue';

type Id = number | string;

interface PaymentRow {
  id?: Id;
  period_name?: string;
  rule_description?: string;
  frequency?: string;
  amount?: number;
  deductions_total?: number;
  status?: string;
  payment_date?: string;
  receipt_url?: string;
  [key: string]: unknown;
}

interface CellParams {
  value?: unknown;
  data?: PaymentRow;
}

interface ButtonClickPayload {
  value?: unknown;
  data: PaymentRow;
  action: string;
}

interface DataTableInstance {
  loadData: () => void;
}

const emit = defineEmits<{
  view: [payment: PaymentRow];
}>();

const { t } = useI18n();
const { myExecutions } = useEmployeePayroll();

const payments = ref<PaymentRow[]>([]);
const datatableRef = ref<DataTableInstance | null>(null);

const formatCurrency = (value: unknown): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

const frequencyLabel = (value: unknown): string => {
  const labels: Record<string, string> = {
    weekly: t('Semanal'),
    biweekly: t('Quinzenal'),
    monthly: t('Mensal')
  };
  const v = String(value ?? '');
  return labels[v] || v;
};

const tableColumns = computed(() => [
  { data: 'period_name', title: t('Período'), sortable: true, flex: 2 },
  { data: 'rule_description', title: t('Regra de pagamento'), sortable: true, flex: 2 },
  {
    data: 'frequency',
    title: t('Frequência'),
    sortable: true,
    cellRenderer: (params: CellParams): string => frequencyLabel(params.value)
  },
  {
    data: 'amount',
    title: t('Valor bruto'),
    sortable: true,
    cellRenderer: (params: CellParams): string => formatCurrency(params.value)
  },
  {
    data: 'deductions_total',
    title: t('Deduções'),
    sortable: true,
    cellRenderer: (params: CellParams): string => {
      if (!params.value || params.value === 0) return '-';
      return formatCurrency(params.value);
    }
  },
  {
    data: 'net_amount',
    title: t('Valor líquido'),
    sortable: true,
    cellRenderer: (params: CellParams): string => {
      const amount = Number(params.data?.amount) || 0;
      const deductions = Number(params.data?.deductions_total) || 0;
      return formatCurrency(amount - deductions);
    }
  },
  {
    data: 'status',
    title: t('Status'),
    sortable: true,
    cellRenderer: (params: CellParams): string => {
      const severities: Record<string, string> = {
        calculated: 'info',
        paid: 'success',
        reversed: 'danger'
      };
      const value = String(params.value ?? '');
      const severity = severities[value] || 'secondary';
      const label = value ? t(value.charAt(0).toUpperCase() + value.slice(1)) : '';
      return `<span class="status-badge status-${severity}">${label}</span>`;
    }
  },
  { data: 'payment_date', title: t('Data de pagamento'), sortable: true },
  {
    data: 'receipt_url',
    title: t('Comprovante'),
    sortable: false,
    cellRenderer: (params: CellParams): string => {
      if (params.value) {
        return '<i class="pi pi-file-pdf" style="color: var(--green-500);"></i>';
      }
      return '<span class="p-text-secondary">-</span>';
    }
  },
  {
    data: null,
    title: t('Ações'),
    sortable: false,
    width: 100,
    renderType: 'action_buttons',
    buttons: [
      {
        icon: 'pi pi-eye',
        class: 'view-btn',
        action: 'view',
        tooltip: t('Ver detalhes')
      }
    ]
  }
]);

const datatableConfig = computed(() => ({
  columns: tableColumns.value,
  rowTestId: (data: PaymentRow) => {
    const id = data?.id;
    return id == null ? '' : `employee-payments-row-${id}`;
  },
  dataKey: 'id',
  pageLength: 25,
  pagination: true,
  sortable: true,
  resizableColumns: true
}));

const handleButtonClick = ({ data, action }: ButtonClickPayload): void => {
  if (action === 'view') {
    emit('view', data);
  }
};

const loadPayments = async (): Promise<void> => {
  log('EmployeePaymentsTable', 'loadPayments', 'Loading employee payments');

  try {
    const userId = permissionsService.getUser()?.sub;
    payments.value = (await myExecutions(userId)) as unknown as PaymentRow[];

    log('EmployeePaymentsTable', 'loadPayments', 'Payments loaded', {
      count: payments.value.length
    });
  } catch (error) {
    console.error('Error loading payments:', error);
  }
};

onMounted(() => {
  loadPayments();
  eventBus.on('payroll-period.calculated', loadPayments);
  eventBus.on('payroll-period.paid', loadPayments);
  eventBus.on('receipt.uploaded', loadPayments);
});

onUnmounted(() => {
  eventBus.off('payroll-period.calculated', loadPayments);
  eventBus.off('payroll-period.paid', loadPayments);
  eventBus.off('receipt.uploaded', loadPayments);
});
</script>

<style scoped>
.employee-payments-table {
  width: 100%;
}

:deep(.status-badge) {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: capitalize;
}

:deep(.status-info) {
  background-color: var(--blue-100);
  color: var(--blue-700);
}

:deep(.status-success) {
  background-color: var(--green-100);
  color: var(--green-700);
}

:deep(.status-danger) {
  background-color: var(--red-100);
  color: var(--red-700);
}
</style>
